import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadHealth, clearHealth } from "../../actions/healthAction";
import { loadOutdoor } from "../../actions/outDoorAction";
import { loadPerformance } from "../../actions/performanceAction";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import TimeRange from "@marenaud/react-timeline-range-slider";
import moment from "moment";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper/modules";
import CircularProgress from "@mui/material/CircularProgress";
import { CardContent, Card, Tabs, Tab } from "@mui/material";
import StackedLineChartOutlinedIcon from "@mui/icons-material/StackedLineChartOutlined";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import Co2OutlinedIcon from "@mui/icons-material/Co2Outlined";
import PieChartOutlinedIcon from "@mui/icons-material/PieChartOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import { motion } from "framer-motion";
import PanZoomController from "../../layouts/PanZoomController";
import CustomTooltip from "../../layouts/CustomTooltip";
import {
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ScatterChart,
  AreaChart,
  Area,
  ReferenceLine,
  ReferenceArea,
  Label,
} from "recharts";
import { useNavigate } from "react-router-dom";
import HealthImage from "./components/HealthImage";
import LineCharts from "../../layouts/LineChart";
import Export from "../../layouts/Export";
import HPI from "../../layouts/HPI";
import Toogle from "../../layouts/Toogle";
import TemperatureKPI from "./components/TemperatureKPI";
import AQIKPI from "./components/AQIKPI";
import HumidityKPI from "./components/HumidityKPI";
import PM10KPI from "./components/PM10KPI";
import PM25KPI from "./components/PM25KPI";
import OzoneKPI from "./components/OzoneKPI";
import Co2KPI from "./components/Co2KPI";
import CoKPI from "./components/CoKPI";
import No2KPI from "./components/No2KPI";
import RadonKPI from "./components/RadonKPI";
import OutdoorTemperatureKPI from "./components/OutdoorTemperatureKPI";
import OutdoorAQIKPI from "./components/OutdoorAQIKPI";
import OutdoorHumidityKPI from "./components/OutdoorHumidityKPI";
import OutdoorPM25KPI from "./components/OutdoorPM25KPI";
import OutdoorPM10KPI from "./components/OutdoorPM10KPI";
import CustomizedAreaChart from "../../layouts/CustomizedAreaChart";
import ZoomController from "../../layouts/ZoomController";

// your forceUpdate hook
const useForceUpdate = () => {
  const [value, setValue] = useState(0); // integer state
  return () => setValue((value) => value + 1); // update the state to force render
};

// Style for the Carousel
const cardStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "200px",
  height: "200px",
  backgroundColor: "#072545",
  border: "none",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
};

const cardContentStyle = {
  display: "flex",
  width: "100%",
  height: "120%",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
};

const breakpoints = {
  // when window width is >= 768px
  768: {
    slidesPerView: 3,
    spaceBetween: 20,
  },
  // when window width is >= 992px
  992: {
    slidesPerView: 4,
    spaceBetween: 30,
  },
  // when window width is >= 1200px
  1200: {
    slidesPerView: 6,
    spaceBetween: 40,
  },
  // when window width is >= 1400px
  1400: {
    slidesPerView: 7,
    spaceBetween: 50,
  },
};

const Health = () => {
  let navigate = useNavigate();
  const forceUpdate = useForceUpdate();
  const dispatch = useDispatch();
  const [selectedPattern, setSelectedPattern] = useState(4);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [swiper, setSwiper] = useState(null);
  const swiperRef = useRef(null);
  const panZoomRef = useRef(null);
  const [selectedKPI, setSelectedKPI] = useState("");
  const [date, setDate] = useState([
    moment().subtract(5, "days"),
    moment(), // 5 days ago, current time
  ]);
  const [selectedTime, setSelectedTime] = useState([
    moment().subtract(5, "days"),
    moment(), // 5 days ago, current time
  ]);

  const [maxDate, setMaxDate] = useState(moment().startOf("day"));
  const [minDate, setMinDate] = useState(moment().subtract(5, "days"));

  const [error, setError] = useState(false);
  const [room, setRoom] = useState("true");
  const [rooms, setRooms] = useState([]);
  const [selectedAllRooms, setSelectedAllRooms] = useState(null);
  const dark = useSelector((state) => state.dark.data);
  const health = useSelector((state) => state.health.data);
  const healthLoading = useSelector((state) => state.health.loading);
  const outdoor = useSelector((state) => state.outdoor.data);
  const dashboard = useSelector((state) => state.dashboard.data);
  const performance = useSelector((state) => state.performance.data);
  const building = useSelector((state) => state.building.data);
  const floor = useSelector((state) => state.floor.data);
  const user = useSelector((state) => state.auth.user);
  const [filteredHealth, setFilteredHealth] = useState(null);
  const [filteredOutdoor, setFilteredOutdoor] = useState(null);
  const [filteredPerformance, setFilteredPerformance] = useState(null);
  const [tempUnit, setTempUnit] = useState(true);
  const [tooltipRoom, setTooltipRoom] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showDetail, setShowDetail] = useState(false);

  // State for tick number of the Time Range
  const [ticksNumber, setTicksNumber] = useState(
    window.innerWidth < 1285 ? 4 : 10
  );

  // State of the Modal Chart
  const [modalChart, setModalChart] = useState(null);
  const [chartName, setChartName] = useState(null);

  const handleZoomIn = () => {
    if (panZoomRef.current) {
      panZoomRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (panZoomRef.current) {
      panZoomRef.current.zoomOut();
    }
  };

  const handleReset = () => {
    if (panZoomRef.current) {
      panZoomRef.current.reset();
    }
  };

  // Change between group KPI tabs
  const handleTabClick = (index) => {
    if (swiper) {
      swiper.slideTo(index);
    }

    setSelectedTabIndex(index);
  };

  // Helper functions: Group Metric by Room ID
  const groupData = (params, arr) => {
    const obj = {};
    for (let i = 0; i < arr.length; i++) {
      if (obj[arr[i].roomID] === undefined) {
        obj[arr[i].roomID] = {
          avg: arr[i][params],
          count: 1,
        };
      } else {
        obj[arr[i].roomID].avg += arr[i][params];
        obj[arr[i].roomID].count++;
      }
    }
    return obj;
  };

  const productivityPattern = (arr) => {
    var lightGroup;
    var CoGroup;
    var PM25Group;

    if (arr.Light) {
      lightGroup = groupData("Light", arr.Light);
    }

    if (arr.CO) {
      CoGroup = groupData("CO", arr.CO);
    }

    if (arr.PM25) {
      PM25Group = groupData("PM25", arr.PM25);
    }

    const combined = Object.keys(lightGroup).reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          Light: { avg: lightGroup[key].avg, count: lightGroup[key].count },
          CO: { avg: CoGroup[key].avg, count: CoGroup[key].count },
          PM25: { avg: PM25Group[key].avg, count: PM25Group[key].count },
        },
      }),
      {}
    );

    console.log("Test combined", combined);

    return combined;
  };

  const vapingPattern = (arr) => {
    var TVOCGroup;
    var eCO2Group;
    var PM10Group;

    if (arr.TVOC) {
      TVOCGroup = groupData("TVOC", arr.TVOC);
    }

    if (arr.eCO2) {
      eCO2Group = groupData("eCO2", arr.eCO2);
    }

    if (arr.PM10) {
      PM10Group = groupData("PM10", arr.PM10);
    }

    const combined = Object.keys(TVOCGroup).reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          TVOC: { avg: TVOCGroup[key].avg, count: TVOCGroup[key].count },
          eCO2: { avg: eCO2Group[key].avg, count: eCO2Group[key].count },
          PM10: { avg: PM10Group[key].avg, count: PM10Group[key].count },
        },
      }),
      {}
    );

    return combined;
  };

  const efficiencyPattern = (arr1, arr2) => {
    var lightGroup;
    var temperatureGroup;
    var numberOfPersonGroup;

    if (arr1 && arr1.Light) {
      lightGroup = groupData("Light", arr1.Light);
    }

    if (arr1 && arr1.Temperature) {
      temperatureGroup = groupData("Temperature", arr1.Temperature);
    }

    if (arr2 && arr2.NumberOfPersons) {
      numberOfPersonGroup = groupData("NumberOfPersons", arr2.NumberOfPersons);
    }

    const combined = Object.keys(lightGroup || {}).reduce((acc, key) => {
      return {
        ...acc,
        [key]: {
          Light:
            lightGroup && lightGroup[key]
              ? { avg: lightGroup[key].avg, count: lightGroup[key].count }
              : { avg: 0, count: 0 },
          Temperature:
            temperatureGroup && temperatureGroup[key]
              ? {
                  avg: temperatureGroup[key].avg,
                  count: temperatureGroup[key].count,
                }
              : { avg: 0, count: 0 },
          NumberOfPersons:
            numberOfPersonGroup && numberOfPersonGroup[key]
              ? {
                  avg: numberOfPersonGroup[key].avg,
                  count: numberOfPersonGroup[key].count,
                }
              : { avg: 0, count: 0 },
        },
      };
    }, {});

    return combined;
  };

  // Helper functions: Multigraph, avg, max, min for recharts and format data for export to csv
  const multiGraph = (data) => {
    let arr = [];
    for (let i = 0; i < data.PM10.length; i++) {
      arr.push({
        PM10: data.PM10[i].PM10,
        "PM2.5": data.PM25[i].PM25,
        enqueuedTime_Stamp: data.PM10[i].enqueuedTime_Stamp,
      });
    }
    return arr;
  };

  const average = (data, param, exact) => {
    let avg = 0;
    let count = 0;
    if (data?.length === 0) return "N/A";
    for (let i = 0; i < data?.length; i++) {
      avg += data[i][param];
      count++;
    }
    return exact
      ? (Math.round((avg / count) * 100) / 100).toFixed(2)
      : Math.round(avg / count);
  };

  const max = (data, param, exact) => {
    let max = -10000000000;
    for (let i = 0; i < data?.length; i++) max = Math.max(max, data[i][param]);
    return max === -10000000000
      ? "N/A"
      : exact
      ? (Math.round(max * 100) / 100).toFixed(2)
      : Math.round(max);
  };

  const min = (data, param, exact) => {
    let min = 10000000000;
    for (let i = 0; i < data?.length; i++) min = Math.min(min, data[i][param]);

    return min === 10000000000
      ? "N/A"
      : exact
      ? (Math.round(min * 100) / 100).toFixed(2)
      : Math.round(min);
  };

  // Format Data
  const format = (obj) => {
    let arr = [];
    for (let i = 0; i < obj.Temperature?.length; i++) {
      const utcFormatted = moment
        .utc(obj["Temperature"][i].enqueuedTime_Stamp)
        .format("YYYY-MM-DD HH:mm:ss");

      const localTimeZone = moment.tz.guess();
      console.log("What is my local timezone", localTimeZone)

      const localTimeFormatted = moment
        .utc(obj["Temperature"][i].enqueuedTime_Stamp)
        .tz(localTimeZone)
        .format("YYYY-MM-DD HH:mm:ss");

      arr.push({
        Room: obj.Temperature[i].room,
        "Temperature °C": obj.Temperature[i].Temperature,
        "Temperature ℉": (obj.Temperature[i].Temperature * 9) / 5 + 32,
        AQI: obj.AQI[i].AQI,
        PM10: obj.PM10[i].PM10,
        PM25: obj.PM25[i].PM25,
        "Relative Humidity": obj.Relative_Humidity[i].Relative_Humidity,
        "Noise Level": obj.Noise_Level[i].Noise_Level,
        "Gas Resistance": obj.Gas_Resistance[i].Gas_Resistance,
        Pressure: obj.Pressure[i].Pressure,
        Light: obj.Light[i].Light,
        CO: obj.CO[i].CO,
        TVOC: obj.TVOC[i].TVOC,
        eCO2: obj.eCO2[i].eCO2,
        NO2: obj.NO2[i].NO2,
        O3: obj.O3[i].O3,
        Radon: obj.Radon[i].Radon,
        timestamp: new Date(obj.Light[i].enqueuedTime_Stamp).toISOString(),
        "UTC Time (+00:00)": utcFormatted,
        "Local Time": localTimeFormatted,
      });
    }
    return arr;
  };

  //Calculate averages
  const averages = useMemo(() => {
    return {
      Temperature: filteredHealth
        ? average(filteredHealth.Temperature, "Temperature")
        : "N/A",
      Relative_Humidity: filteredHealth
        ? average(filteredHealth.Relative_Humidity, "Relative_Humidity")
        : "N/A",
      AQI: filteredHealth ? average(filteredHealth.AQI, "AQI") : "N/A",
      PM10: filteredHealth ? average(filteredHealth.PM10, "PM10") : "N/A",
      PM25: filteredHealth ? average(filteredHealth.PM25, "PM25") : "N/A",
      TVOC: filteredHealth ? average(filteredHealth.TVOC, "TVOC") : "N/A",
      eCO2: filteredHealth ? average(filteredHealth.eCO2, "eCO2") : "N/A",
      CO: filteredHealth ? average(filteredHealth.CO, "CO") : "N/A",
      NO2: filteredHealth ? average(filteredHealth.NO2, "NO2", true) : "N/A",
      O3: filteredHealth ? average(filteredHealth.O3, "O3", true) : "N/A",
      HPI: filteredHealth ? average(filteredHealth.HPI, "HPI", true) : "N/A",
      Radon: filteredHealth
        ? average(filteredHealth.Radon, "Radon", true)
        : "N/A",
      // Outdoor
      TemperatureOutdoor: filteredOutdoor
        ? average(filteredOutdoor.Temperature, "Temperature")
        : "N/A",
      Dewpoint_Temperature: filteredOutdoor
        ? average(filteredOutdoor.Dewpoint_Temperature, "Dewpoint_Temperature")
        : "N/A",
      Pressure: filteredOutdoor
        ? average(filteredOutdoor.Pressure, "Pressure")
        : "N/A",
      Relative_HumidityOutdoor: filteredOutdoor
        ? average(filteredOutdoor.Relative_Humidity, "Relative_HumidityOutdoor")
        : "N/A",
      OutdoorAQI: filteredOutdoor
        ? average(filteredOutdoor.OutdoorAQI, "OutdoorAQI")
        : "N/A",
      OutdoorPM10: filteredOutdoor
        ? average(filteredOutdoor.PM10, "PM10")
        : "N/A",
      OutdoorPM25: filteredOutdoor
        ? average(filteredOutdoor.PM25, "PM25")
        : "N/A",
    };
  }, [filteredHealth, filteredOutdoor]);

  // const fToC = (fahrenheit) => Math.round(((fahrenheit - 32) * 5) / 9);

  const cToF = (data) => {
    const formatHealth = { ...data };
    formatHealth["Temperature"] = formatHealth["Temperature"]?.map((item) => {
      return {
        ...item,
        Temperature: Math.round((item.Temperature * 9) / 5 + 32),
      };
    });

    return formatHealth;
  };

  // Effect on initial load:if user is not authenticated or building or floor not selected, navigate to dashboard page
  useEffect(() => {
    if (!dashboard || !user || !floor || !building) {
      return navigate("/dashboard");
    }
  }, [floor, building, user]);

  // Effect on initial load: When first loading the page, run the fetch Health data
  useEffect(() => {
    if (health === undefined && user) {
      dispatch(
        loadHealth(
          `between=${date[0]}--${
            date[1]
          }&params=Temperature,AQI,PM10,PM25,CO,NO2,eCO2,TVOC,Relative_Humidity,Noise_Level,Gas_Resistance,Pressure,Light,O3,Radon&${
            building ? `&building=${building?.id}` : ""
          }${floor ? `&floor=${floor?.id}` : ""}`
        )
      );

      if (user.client_id === 2) {
        setTempUnit(true);
      } else {
        setTempUnit(false);
      }
    }
  }, [loadHealth, user]);

  // Effect on initial load: When first loading the page, run the fetch Outdoor data
  useEffect(() => {
    if (outdoor === undefined && user) {
      dispatch(
        loadOutdoor(
          `between=${date[0]}--${
            date[1]
          }&params=Temperature,Dewpoint_Temperature,Pressure,OutdoorAQI,PM10,PM25,PM1,Relative_Humidity&${
            building ? `&building=${building?.id}` : ""
          }${floor ? `&floor=${floor?.id}` : ""}&room=5259`
        )
      );
    }
  }, [loadOutdoor, dispatch, user]);

  // Effect when selecting pattern tabs, run the fetch Performance data
  useEffect(() => {
    if (selectedTabIndex === 4) {
      dispatch(
        loadPerformance(
          `between=${date[0]}--${
            date[1]
          }&params=NumberOfPersons,NumberOfEmptySeats,DateTime_Stamp&user=${
            user?.username
          }${building ? `&building=${building?.id}` : ""}${
            floor ? `&floor=${floor?.id}` : ""
          }${room ? `&room=${room?.id}` : ""}`
        )
      );
    }
  }, [selectedTabIndex]);

  // Effect on load health and filtered health data
  useEffect(() => {
    if (health && !filteredHealth) {
      const formatData = cToF(health);
      setFilteredHealth(formatData);
    }
    if (!health && filteredHealth) {
      const formatData = cToF(health);
      setFilteredHealth(formatData);
    }
  }, [health, filteredHealth]);

  // When toogle between Temp Unit
  useEffect(() => {
    if (tempUnit && health) {
      const formatData = cToF(health);
      setFilteredHealth(formatData);
    } else {
      setFilteredHealth(health);
    }
  }, [tempUnit, health]);

  // Effect to reset the data based on selection of the date time slider
  useEffect(() => {
    if (health) {
      if (health.dateTimeRange) {
        setDate([
          moment(health.dateTimeRange.min),
          moment(health.dateTimeRange.max),
        ]);
        setSelectedTime([
          moment(health.dateTimeRange.min),
          moment(health.dateTimeRange.max),
        ]);
        setMinDate(moment(health.dateTimeRange.validMin));
        setMaxDate(moment(health.dateTimeRange.validMax));
      }
    }
  }, [health, selectedAllRooms]);

  // Effect on load outdoor and filtered outdoor data
  useEffect(() => {
    if (outdoor && !filteredOutdoor) {
      setFilteredOutdoor(outdoor);
    }
    if (!outdoor && filteredOutdoor) {
      setFilteredOutdoor(outdoor);
    }
  }, [outdoor, filteredOutdoor]);

  // Effect on load performance and filtered performance
  useEffect(() => {
    if (performance && !filteredPerformance) {
      setFilteredPerformance(performance);
    }
    if (!performance && filteredPerformance) {
      setFilteredPerformance(performance);
    }
  }, [performance, filteredPerformance]);

  // Effect on modal chart when select different time interval
  useEffect(() => {
    if (filteredHealth && modalChart) {
      setModalChart({
        name: chartName.name,
        type: chartName.type,
        label: chartName.label,
        color: "#FEC14B",
        unit: chartName.unit,
        x: "enqueuedTime_Stamp",
        y:
          chartName?.name === "Relative_HumidityOutdoor"
            ? "Relative_Humidity"
            : chartName?.name === "OutdoorTemperature"
            ? "Temperature"
            : chartName?.name === "OutdoorPM25"
            ? "PM25"
            : chartName?.name === "OutdoorPM10"
            ? "PM10"
            : chartName.name,
        description:
          "It's the indoor temperature , that measured to reflects the employee comfort.",
        areas: chartName?.areas ? chartName.areas : null,
        lines: chartName?.lines ? chartName.lines : null,
        data:
          chartName?.name === "Relative_HumidityOutdoor"
            ? filteredOutdoor["Relative_Humidity"]
            : chartName?.name === "OutdoorTemperature"
            ? filteredOutdoor["Temperature"]
            : chartName?.name === "OutdoorPM10"
            ? filteredOutdoor["PM10"]
            : chartName?.name === "OutdoorPM25"
            ? filteredOutdoor["PM25"]
            : chartName?.type === "outdoor"
            ? filteredOutdoor[chartName.name]
            : filteredHealth[chartName.name],
      });
    }
  }, [filteredHealth, filteredOutdoor]);

  // Effect on modal chart when select different temp unit
  useEffect(() => {
    if (filteredHealth && modalChart && tempUnit !== null) {
      setChartName({
        name: "Temperature",
        type: "indoor",
        label: "Temperature",
        unit: `${tempUnit ? "℉" : "°C"}`,
        areas: [
          {
            color: "green",
            y1: `${tempUnit ? "64.4" : "18"}`,
            y2: `${tempUnit ? "71.6" : "22"}`,
            label: `between ${tempUnit ? "64.4" : "18"} & ${
              tempUnit ? "71.6" : "22"
            } ${tempUnit ? "℉" : "°C"}`,
          },
        ],
      });
      setModalChart({
        name: "Temperature",
        type: "indoor",
        label: "Temperature",
        color: "#FEC14B",
        unit: `${tempUnit ? "℉" : "°C"}`,
        x: "enqueuedTime_Stamp",
        y: "Temperature",
        description:
          "It's the indoor temperature , that measured to reflects the employee comfort.",
        areas: [
          {
            color: "green",
            y1: `${tempUnit ? "64.4" : "18"}`,
            y2: `${tempUnit ? "71.6" : "22"}`,
            label: `between ${tempUnit ? "64.4" : "18"} & ${
              tempUnit ? "71.6" : "22"
            } ${tempUnit ? "℉" : "°C"}`,
          },
        ],
        data: filteredHealth?.Temperature,
      });
    }
  }, [filteredHealth, tempUnit]);

  // Effect on screen change, adjust the number of tick for the time range slider
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1285px)");
    const handleMediaChange = () => setTicksNumber(mediaQuery.matches ? 5 : 10);

    mediaQuery.addListener(handleMediaChange);

    return () => {
      mediaQuery.removeListener(handleMediaChange);
    };
  }, []);

  // Effect to initially set all rooms selected on heath heatmap
  useEffect(() => {
    setSelectedAllRooms("true");
  }, []);

  return (
    <>
      <div
        className="dashboard h-100 w-100 overflow-y-auto"
        style={{
          backgroundImage: `url('./images/building${
            dark ? "" : "-Light"
          }.png')`,
        }}
      >
        <div className="container-fluid px-0 pb-0 pt-4  h-100 w-100">
          {/* Upper part */}
          <div className="row w-100 h-66 flex-column flex-lg-row m-0 align-items-center">
            {/* Heatmap Layout */}
            <div
              className="col col-lg-6 h-100 d-flex flex-column justify-content-center"
              style={{ position: "relative" }}
            >
              <CustomTooltip
                selectedKPI={selectedKPI}
                showDetail={showDetail}
                rooms={floor?.rooms}
                tooltipRoom={tooltipRoom}
              />
              {/* HPI Layout */}
              <div className="d-flex align-items-center w-100 justify-content-center">
                <HPI
                  averages={averages}
                  setModalChart={setModalChart}
                  filteredHealth={filteredHealth}
                  modalChart={modalChart}
                  dark={dark}
                />
              </div>

              {/* Selected floor and Rooms Selection  */}
              {floor && (
                <div className="d-flex flex-column justify-content-center align-items-center">
                  <div
                    className={`text-center ${
                      dark ? "text-white" : "text-main"
                    } fs-4`}
                  >
                    {floor.floor}
                  </div>

                  {/* Radio selection */}
                  <div className="d-flex justify-content-between gap-4 text-white">
                    {/* Select All */}
                    <div>
                      <label className="d-flex justify-content-center align-items-center gap-1">
                        <input
                          type="radio"
                          value={true}
                          checked={selectedAllRooms === "true"}
                          onChange={(e) => {
                            setSelectedAllRooms(e.target.value);
                          }}
                        />
                        Select all
                      </label>
                    </div>

                    {/* Deselect All */}
                    <div>
                      <label className="d-flex justify-content-center align-items-center gap-1">
                        <input
                          type="radio"
                          value={false}
                          checked={selectedAllRooms === "false"}
                          onChange={(e) => {
                            setSelectedAllRooms(e.target.value);
                          }}
                        />
                        Deselect all
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Zoom Controller Heatmap */}
              <div className="w-100 d-flex align-items-center justify-content-center mt-2">
                <ZoomController
                  zoomIn={handleZoomIn}
                  zoomOut={handleZoomOut}
                  reset={handleReset}
                />
              </div>

              {/* Heatmap Image */}
              <div className="w-100 h-100 text-center mb-lg-0 mb-5 d-flex flex-column align-items-center justify-content-between overflow-hidden">
                {floor && (
                  <>
                    <PanZoomController panZoomRef={panZoomRef}>
                      <HealthImage
                        building={building}
                        floor={floor}
                        rooms={rooms}
                        selectedAllRooms={selectedAllRooms}
                        setRooms={setRooms}
                        setSelectedAllRooms={setSelectedAllRooms}
                        setRoom={setRoom}
                        loadHealth={loadHealth}
                        setSelectedTime={setSelectedTime}
                        selectedPattern={selectedPattern}
                        selectedKPI={selectedKPI}
                        date={date}
                        setTooltipRoom={setTooltipRoom}
                        setTooltipPosition={setTooltipPosition}
                        showDetail={showDetail}
                        setShowDetail={setShowDetail}
                        tempUnit={tempUnit}
                        productivityPattern={
                          filteredHealth && floor
                            ? productivityPattern(filteredHealth)
                            : {}
                        }
                        vapingPattern={
                          filteredHealth && floor
                            ? vapingPattern(filteredHealth)
                            : {}
                        }
                        efficiencyPattern={
                          filteredPerformance && floor
                            ? efficiencyPattern(
                                filteredHealth,
                                filteredPerformance
                              )
                            : {}
                        }
                        heat={
                          filteredHealth && floor
                            ? groupData(
                                "Temperature",
                                filteredHealth.Temperature
                              )
                            : {}
                        }
                        aqi={
                          filteredHealth && floor
                            ? groupData("AQI", filteredHealth.AQI)
                            : {}
                        }
                        humidity={
                          filteredHealth && floor
                            ? groupData(
                                "Relative_Humidity",
                                filteredHealth.Relative_Humidity
                              )
                            : {}
                        }
                        pm10={
                          filteredHealth && floor
                            ? groupData("PM10", filteredHealth.PM10)
                            : {}
                        }
                        pm25={
                          filteredHealth && floor
                            ? groupData("PM25", filteredHealth.PM25)
                            : {}
                        }
                        noise={
                          filteredHealth && floor
                            ? groupData(
                                "Noise_Level",
                                filteredHealth.Noise_Level
                              )
                            : {}
                        }
                        light={
                          filteredHealth && floor
                            ? groupData("Light", filteredHealth.Light)
                            : {}
                        }
                        tvoc={
                          filteredHealth && floor
                            ? groupData("TVOC", filteredHealth.TVOC)
                            : {}
                        }
                        o3={
                          filteredHealth && floor
                            ? groupData("O3", filteredHealth.O3)
                            : {}
                        }
                        co2={
                          filteredHealth && floor
                            ? groupData("eCO2", filteredHealth.eCO2)
                            : {}
                        }
                        co={
                          filteredHealth && floor
                            ? groupData("CO", filteredHealth.CO)
                            : {}
                        }
                        no2={
                          filteredHealth && floor
                            ? groupData("NO2", filteredHealth.NO2)
                            : {}
                        }
                        radon={
                          filteredHealth && floor
                            ? groupData("Radon", filteredHealth.Radon)
                            : {}
                        }
                      />
                    </PanZoomController>
                  </>
                )}
              </div>
              {/* <div
                className={`text-center ${
                  dark ? "text-white" : "text-main"
                } fs-4`}
              >
                {room ? room.Room : "All rooms"}
              </div> */}
            </div>

            {/* MUltiChart Area */}
            <div className="col col-lg-6 h-100 d-flex flex-column px-2 px-lg-3">
              <div className="w-100 align-items-center d-flex justify-content-between h-25 flex-column flex-lg-row">
                <div className="h-25 d-flex align-items-center justify-content-center w-100 mb-5 mb-lg-0">
                  {/* Time Range selector */}
                  <div className="w-100 d-flex flex-column align-items-center justify-content-center">
                    <div
                      className={`w-100 text-center ${
                        dark ? "text-white" : ""
                      }`}
                    >
                      <span>Selected Interval: </span>
                      <span>{moment(selectedTime[0]).format("HH:mm Do")}</span>
                      <span className="mx-2">-</span>
                      <span>{moment(selectedTime[1]).format("HH:mm Do")}</span>
                    </div>
                    <TimeRange
                      onUpdateCallback={(e) => setError(e.error)}
                      error={error}
                      ticksNumber={ticksNumber}
                      className="text-white"
                      selectedInterval={selectedTime}
                      timelineInterval={date}
                      onChangeCallback={(selectedInterval) => {
                        if (filteredHealth) {
                          const localToUTCFrom = moment(
                            selectedInterval[0]
                          ).format();
                          const localToUTCTo = moment(
                            selectedInterval[1]
                          ).format();

                          let filter = {};

                          for (let key in health) {
                            for (let i = 0; i < health[key].length; i++) {
                              if (filter[key] === undefined) filter[key] = [];
                              let convertTimeStamp = moment(
                                health[key][i].enqueuedTime_Stamp
                              )
                                .local()
                                .format();
                              if (
                                convertTimeStamp >= localToUTCFrom &&
                                convertTimeStamp <= localToUTCTo
                              ) {
                                filter[key].push(health[key][i]);
                              }
                            }
                          }

                          if (tempUnit !== null) {
                            const formatData = tempUnit ? cToF(filter) : filter;
                            setFilteredHealth(formatData);
                          } else {
                            setFilteredHealth(filter);
                          }
                        }

                        if (filteredOutdoor) {
                          let filter = {};

                          for (let key in outdoor) {
                            for (let i = 0; i < outdoor[key].length; i++) {
                              if (filter[key] === undefined) filter[key] = [];
                              if (
                                outdoor[key][i].enqueuedTime_Stamp >=
                                  selectedInterval[0].getTime() &&
                                outdoor[key][i].enqueuedTime_Stamp <=
                                  selectedInterval[1].getTime()
                              ) {
                                filter[key].push(outdoor[key][i]);
                              }
                            }
                          }
                          setFilteredOutdoor(filter);
                        }
                        setSelectedTime(selectedInterval);
                      }}
                      formatTick={(ms) => {
                        return moment(ms).format("HH:mm Do MMM");
                      }}
                    />
                  </div>
                </div>
                {/* Date Selector */}
                <div className="">
                  <DateRangePicker
                    clearIcon={null}
                    calendarIcon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        stroke={dark ? "white" : "black"}
                        stroke-width="2"
                        class="react-daterange-picker__calendar-button__icon react-daterange-picker__button__icon"
                      >
                        <rect
                          fill="none"
                          height="15"
                          width="15"
                          x="2"
                          y="2"
                        ></rect>
                        <line x1="6" x2="6" y1="0" y2="4"></line>
                        <line x1="13" x2="13" y1="0" y2="4"></line>
                      </svg>
                    }
                    onInvalidChange={() =>
                      alert(
                        "The date range picked is out of the project range. Please select the valid date range"
                      )
                    }
                    // minDate={minDate.toDate()}
                    // maxDate={maxDate.toDate()}
                    onChange={(e) => {
                      console.log(e);
                      setDate(e);
                      setSelectedTime(e);
                      dispatch(
                        loadHealth(
                          `between=${e[0]}--${
                            e[1]
                          }&params=Temperature,AQI,PM10,PM25,CO,NO2,eCO2,TVOC,Relative_Humidity,Noise_Level,Gas_Resistance,Pressure,Light,O3,Radon&user=${
                            user.username
                          }${building ? `&building=${building?.id}` : ""}${
                            floor ? `&floor=${floor?.id}` : ""
                          }${
                            room ? `&room=${room?.id}` : ""
                          }&timeRangeSelected=true`
                        )
                      );
                      setFilteredHealth(health);

                      dispatch(
                        loadOutdoor(
                          `between=${e[0]}--${
                            e[1]
                          }&params=Temperature,Dewpoint_Temperature,Pressure,OutdoorAQI,PM10,PM25,PM1,Relative_Humidity&${
                            building ? `&building=${building?.id}` : ""
                          }${floor ? `&floor=${floor?.id}` : ""}&room=5259`
                        )
                      );
                      setFilteredOutdoor(outdoor);
                    }}
                    value={date}
                  />
                </div>
                {/* Download Button */}
                <div className="my-4 my-lg-0">
                  {filteredHealth && filteredHealth?.rawData ? (
                    <Export
                      data={format(filteredHealth.rawData)}
                      name="health"
                      dark={dark}
                    />
                  ):(<CircularProgress
                    size={30}/>)}
                </div>
              </div>

              {/* Chart Area */}
              {health ? (
                <div className="d-flex flex-column overflow-auto">
                  {filteredHealth && health && !modalChart ? (
                    <CustomizedAreaChart
                      data={multiGraph(filteredHealth)}
                      dark={dark}
                    />
                  ) : modalChart && modalChart.name === "TVOC" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "Light" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "Noise_Level" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "Temperature" ? (
                    <>
                      {/* Sort option */}
                      <div className="d-flex align-items-center justify-content-end mr-3">
                        <div className="" style={{ width: "150px" }}>
                          <Toogle
                            checked={tempUnit}
                            setChecked={setTempUnit}
                            labels={["℃", "℉"]}
                          />
                        </div>
                      </div>
                      <LineCharts
                        dataList={modalChart}
                        dark={dark}
                        aspect={3}
                        i={1}
                        key={1}
                        lines={modalChart.lines}
                        areas={modalChart.areas}
                      />
                    </>
                  ) : modalChart && modalChart.name === "AQI" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "Relative_Humidity" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "PM10" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "PM25" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "O3" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "CO2" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "CO" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "NO2" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "Radon" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "OutdoorTemperature" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "OutdoorAQI" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart &&
                    modalChart.name === "Relative_HumidityOutdoor" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "OutdoorPM10" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : modalChart && modalChart.name === "OutdoorPM25" ? (
                    <LineCharts
                      dataList={modalChart}
                      dark={dark}
                      aspect={3}
                      i={1}
                      key={1}
                      lines={modalChart.lines}
                      areas={modalChart.areas}
                    />
                  ) : rooms.length ? (
                    <div className="h-75 d-flex align-items-center justify-content-center w-100">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : null}
                  <p className="d-flex text-center text-white p-2">
                    {modalChart
                      ? modalChart.description
                      : "PM2.5 (particulate matter less than 2.5 micrometers) and PM10 (particulate matter less than 10 micrometers) are air pollutants that are a concern for public health."}
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          {/* Lower Part  */}
          <div
            className="row w-100 flex-column m-0 overflow-hidden"
            style={{ backgroundColor: "#112138" }}
          >
            <div className="col flex flex-column">
              {/* Carousel Navigation */}
              <div className="w-100 mx-auto d-flex flex-row justify-content-center align-items-center mb-2">
                {/* Title */}
                <h5 className="text-white align-self-start pt-1">
                  Monitor Board
                </h5>
                {/* Navigation Tabs */}
                <Tabs
                  value={selectedTabIndex}
                  onChange={(e, index) => handleTabClick(index)}
                  aria-label="icon tabs example"
                  selectionFollowsFocus
                >
                  <Tab
                    sx={{
                      color: "white",
                      fontSize: "10px",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                    icon={
                      <StackedLineChartOutlinedIcon sx={{ color: "white" }} />
                    }
                    aria-label="phone"
                    label="Indoor"
                    iconPosition="top"
                  />
                  <Tab
                    sx={{
                      color: "white",
                      fontSize: "10px",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                    icon={<Co2OutlinedIcon sx={{ color: "white" }} />}
                    aria-label="phone"
                    label="Indoor Gas"
                    iconPosition="top"
                  />
                  <Tab
                    sx={{
                      color: "white",
                      fontSize: "10px",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                    icon={
                      <EnergySavingsLeafOutlinedIcon sx={{ color: "white" }} />
                    }
                    aria-label="phone"
                    label={
                      <div>
                        Environment <br /> Conditions
                      </div>
                    }
                    iconPosition="top"
                  />
                  {/* Show Outdoor and Pattern for GlobalDWS only */}
                  <Tab
                    disabled={building?.id !== 1}
                    sx={{
                      color: "white",
                      fontSize: "10px",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                    icon={
                      building?.id === 1 ? (
                        <PieChartOutlinedIcon sx={{ color: "white" }} />
                      ) : null
                    }
                    aria-label="favorite"
                    label={building?.id === 1 ? "Outdoor" : ""}
                    iconPosition="top"
                  />
                  <Tab
                    disabled={building?.id !== 1}
                    sx={{
                      color: "white",
                      fontSize: "10px",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                    icon={
                      building?.id === 1 ? (
                        <RuleOutlinedIcon sx={{ color: "white" }} />
                      ) : null
                    }
                    aria-label="person"
                    label={building?.id === 1 ? "Pattern" : ""}
                    iconPosition="top"
                  />
                </Tabs>
              </div>

              {/* Carousel*/}
              <Swiper
                ref={swiperRef}
                modules={[Navigation, Pagination, Scrollbar, A11y]}
                spaceBetween={20}
                slidesPerView={1}
                navigation
                // pagination={{ clickable: false}}
                onSlideChange={() => setSelectedTabIndex(swiper.activeIndex)}
                onSwiper={(swiper) => setSwiper(swiper)}
              >
                <>
                  {/* Indoor */}
                  <SwiperSlide>
                    <Swiper
                      centeredSlides={true}
                      centeredSlidesBounds={true}
                      centerInsufficientSlides={true}
                      breakpoints={breakpoints}
                      pagination={{ clickable: true }}
                      scrollbar={{ draggable: true }}
                      onSlideChange={() => console.log("nested slide change")}
                      onSwiper={(swiper) => console.log(swiper)}
                    >
                      <SwiperSlide>
                        {/* Temperature KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "Temperature",
                                type: "indoor",
                                label: "Temperature",
                                unit: `${tempUnit ? "℉" : "°C"}`,
                                areas: [
                                  {
                                    color: "green",
                                    y1: `${tempUnit ? "64.4" : "18"}`,
                                    y2: `${tempUnit ? "71.6" : "22"}`,
                                    label: `between ${
                                      tempUnit ? "64.4" : "18"
                                    } & ${tempUnit ? "71.6" : "22"} ${
                                      tempUnit ? "℉" : "°C"
                                    }`,
                                  },
                                ],
                              });
                              setModalChart({
                                name: "Temperature",
                                type: "indoor",
                                label: "Temperature",
                                color: "#FEC14B",
                                unit: `${tempUnit ? "℉" : "°C"}`,
                                x: "enqueuedTime_Stamp",
                                y: "Temperature",
                                description:
                                  "It's the indoor temperature , that measured to reflects the employee comfort.",
                                areas: [
                                  {
                                    color: "green",
                                    y1: `${tempUnit ? "64.4" : "18"}`,
                                    y2: `${tempUnit ? "71.6" : "22"}`,
                                    label: `between ${
                                      tempUnit ? "64.4" : "18"
                                    } & ${tempUnit ? "71.6" : "22"} ${
                                      tempUnit ? "℉" : "°C"
                                    }`,
                                  },
                                ],
                                data: filteredHealth?.Temperature,
                              });
                              setSelectedKPI("Temperature");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <TemperatureKPI
                                  tempUnit={tempUnit}
                                  avgTemp={averages.Temperature}
                                  maxTemp={max(
                                    filteredHealth.Temperature,
                                    "Temperature",
                                    true
                                  )}
                                  minTemp={min(
                                    filteredHealth.Temperature,
                                    "Temperature",
                                    true
                                  )}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      <SwiperSlide>
                        {/* AQI KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "AQI",
                                type: "indoor",
                                label: "Air Quality",
                                unit: "",
                                lines: [
                                  {
                                    color: "green",
                                    value: 50,
                                    label: "50 and Below",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "AQI",
                                type: "indoor",
                                label: "Air Quality",
                                color: "#FEC14B",
                                description:
                                  "Indoor Air Quality (IAQ) refers to the air quality within and around buildings and structures, especially as it relates to the health and comfort of building occupants. Understanding and controlling common pollutants indoors can help reduce your risk of indoor health concerns.",
                                unit: "",
                                lines: [
                                  {
                                    color: "green",
                                    value: 50,
                                    label: "50 and Below",
                                  },
                                ],
                                x: "enqueuedTime_Stamp",
                                y: "AQI",
                                data: filteredHealth.AQI,
                              });
                              setSelectedKPI("AQI");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <AQIKPI
                                  avgAQI={averages.AQI}
                                  maxAQI={max(filteredHealth.AQI, "AQI", true)}
                                  minAQI={min(filteredHealth.AQI, "AQI", true)}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      <SwiperSlide>
                        {/* Humidity KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "Relative_Humidity",
                                type: "indoor",
                                label: "Relative_Humidity",
                                unit: "%",
                                areas: [
                                  {
                                    color: "green",
                                    y1: "40",
                                    y2: "60",
                                    label: "between 40% and 60%",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "Relative_Humidity",
                                type: "indoor",
                                label: "Humidity",
                                color: "#FEC14B",
                                unit: "%",
                                x: "enqueuedTime_Stamp",
                                y: "Relative_Humidity",
                                description:
                                  "Relative humidity (RH) (expressed as a percent) also measures water vapor, but RELATIVE to the temperature of the air. In other words, it is a measure of the actual amount of water vapor in the air compared to the total amount of vapor that can exist in the air at its current temperature.",
                                areas: [
                                  {
                                    color: "green",
                                    y1: "40",
                                    y2: "60",
                                    label: "between 40% and 60%",
                                  },
                                ],
                                data: filteredHealth.Relative_Humidity,
                              });
                              setSelectedKPI("Humidity");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <HumidityKPI
                                  avgHum={averages.Relative_Humidity}
                                  maxHum={max(
                                    filteredHealth.Relative_Humidity,
                                    "Relative_Humidity",
                                    true
                                  )}
                                  minHum={min(
                                    filteredHealth.Relative_Humidity,
                                    "Relative_Humidity",
                                    true
                                  )}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                      <SwiperSlide>
                        {/* Indoor PM10 KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "PM10",
                                type: "indoor",
                                label: "PM10",
                                unit: "",
                                lines: [
                                  {
                                    color: "green",
                                    value: 200,
                                    label: "50 and Below",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "PM10",
                                type: "indoor",
                                label: "PM10",
                                color: "#FEC14B",
                                description:
                                  "PM10 refers to particulate matter that is less than 10 micrometers in diameter. These particles are small enough to be inhaled and can reach the respiratory system, leading to health problems such as asthma, bronchitis, and other respiratory issues.",
                                unit: "(μ g/m ³)",
                                lines: [
                                  {
                                    color: "green",
                                    value: 15,
                                    label: "15 μ g/m ³ or Below",
                                  },
                                ],
                                x: "enqueuedTime_Stamp",
                                y: "PM10",
                                data: filteredHealth.PM10,
                              });
                              setSelectedKPI("PM10");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <PM10KPI
                                  avgTemp={averages.PM10}
                                  maxTemp={max(
                                    filteredHealth.PM10,
                                    "PM10",
                                    true
                                  )}
                                  minTemp={min(
                                    filteredHealth.PM10,
                                    "PM10",
                                    true
                                  )}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                      <SwiperSlide>
                        {/* Indoor PM25 KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "PM25",
                                type: "indoor",
                                label: "PM2.5",
                                unit: "",
                                lines: [
                                  {
                                    color: "green",
                                    value: 200,
                                    label: "50 and Below",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "PM25",
                                type: "indoor",
                                label: "PM2.5",
                                color: "#FEC14B",
                                description:
                                  "PM2.5 refers to particulate matter that is less than 2.5 micrometers in diameter. These fine particles are small enough to be deeply inhaled into the lungs and can even enter the bloodstream.",
                                unit: "(μ g/m ³)",
                                lines: [
                                  {
                                    color: "green",
                                    value: 15,
                                    label: "15 μ g/m ³ or Below",
                                  },
                                ],
                                x: "enqueuedTime_Stamp",
                                y: "PM25",
                                data: filteredHealth.PM25,
                              });
                              setSelectedKPI("PM25");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <PM25KPI
                                  avgTemp={averages.PM25}
                                  maxTemp={max(
                                    filteredHealth.PM25,
                                    "PM25",
                                    true
                                  )}
                                  minTemp={min(
                                    filteredHealth.PM25,
                                    "PM25",
                                    true
                                  )}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                    </Swiper>
                  </SwiperSlide>

                  {/* Indoor Gas */}
                  <SwiperSlide>
                    <Swiper
                      centeredSlides={true}
                      centeredSlidesBounds={true}
                      centerInsufficientSlides={true}
                      breakpoints={breakpoints}
                      pagination={{ clickable: true }}
                      scrollbar={{ draggable: true }}
                      onSlideChange={() => console.log("nested slide change")}
                      onSwiper={(swiper) => console.log(swiper)}
                    >
                      <SwiperSlide>
                        {/* Ozone KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "O3",
                                type: "indoor",
                                label: "Ozone",
                                unit: "ppm",
                                lines: [
                                  {
                                    color: "green",
                                    value: 0.051,
                                    label: "0.051 ppm or lower",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "O3",
                                type: "indoor",
                                label: "Ozone",
                                color: "#FEC14B",
                                lines: [
                                  {
                                    color: "green",
                                    value: 0.051,
                                    label: "0.051 ppm or lower",
                                  },
                                ],
                                unit: "ppm",
                                x: "enqueuedTime_Stamp",
                                y: "O3",
                                description:
                                  "Triatomic form of oxygen. Ozone is hazardous to the respiratory system at ground level but a layer in the upper atmosphere blocks much of the ultraviolet radiation from the sun.​",
                                data: filteredHealth.O3,
                              });
                              setSelectedKPI("O3");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <OzoneKPI
                                  avgOZ={averages.O3}
                                  maxOZ={max(filteredHealth.O3, "O3", true)}
                                  minOZ={min(filteredHealth.O3, "O3", true)}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                      <SwiperSlide>
                        {/* Carbon dioxide KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "CO2",
                                type: "indoor",
                                label: "Carbon dioxide",
                                unit: "ppm",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 900,
                                    label: "900 ppb or lower​",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "CO2",
                                type: "indoor",
                                label: "Carbon dioxide",
                                color: "#FEC14B",
                                unit: "ppm",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 900,
                                    label: "900 ppb or lower​",
                                  },
                                ],
                                x: "enqueuedTime_Stamp",
                                y: "eCO2",
                                data: filteredHealth.eCO2,
                                description:
                                  "A colorless odorless gas at atmospheric temperatures and pressures, can cause headaches, dizziness, restlessness, a tingling or pins or needles feeling, difficulty breathing​.",
                              });
                              setSelectedKPI("eCO2");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <Co2KPI
                                  avgCO2={averages.eCO2}
                                  maxCO2={max(
                                    filteredHealth.eCO2,
                                    "eCO2",
                                    true
                                  )}
                                  minCO2={min(
                                    filteredHealth.eCO2,
                                    "eCO2",
                                    true
                                  )}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      <SwiperSlide>
                        {/* Carbon monoxide KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "CO",
                                type: "indoor",
                                label: "Carbon monoxide",
                                unit: "ppm",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 9,
                                    label: "9 ppm or lower",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "CO",
                                type: "indoor",
                                label: "Carbon monoxide",
                                color: "#FEC14B",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 9,
                                    label: "9 ppm or lower",
                                  },
                                ],
                                unit: "ppm",
                                x: "enqueuedTime_Stamp",
                                y: "CO",
                                data: filteredHealth.CO,
                                description:
                                  "A colorless, odorless and highly poisonous gas formed by Incomplete combustion that replaces oxygen in hemoglobin, limiting the blood's ability to deliver oxygen, which can lead to death.​",
                              });
                              setSelectedKPI("CO");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <CoKPI
                                  avgCO={averages.CO}
                                  maxCO={max(filteredHealth.CO, "CO", true)}
                                  minCO={min(filteredHealth.CO, "CO", true)}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                      <SwiperSlide>
                        {/* Nitrogen dioxide KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "NO2",
                                type: "indoor",
                                label: "Nitrogen dioxide",
                                unit: "ppm",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 0.021,
                                    label: "0.021 ppm or lower",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "NO2",
                                type: "indoor",
                                label: "Nitrogen dioxide",
                                color: "#FEC14B",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 0.021,
                                    label: "0.021 ppm or lower",
                                  },
                                ],
                                unit: "ppm",
                                x: "enqueuedTime_Stamp",
                                y: "NO2",
                                data: filteredHealth.NO2,
                                description:
                                  "One of a group of highly reactive gases, can cause damage to the human respiratory tract and increase a person's vulnerability to, and the severity of, respiratory infections and asthma.​",
                              });
                              setSelectedKPI("NO2");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <No2KPI
                                  avgNO2={averages.NO2}
                                  maxNO2={max(filteredHealth.NO2, "NO2", true)}
                                  minNO2={min(filteredHealth.NO2, "NO2", true)}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                      <SwiperSlide>
                        {/* Radon KPI */}
                        {filteredHealth && health ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "Radon",
                                type: "indoor",
                                label: "Radon",
                                unit: "Bq/m3",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 150,
                                    label: "150 Bq/m3 or lower",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "Radon",
                                type: "indoor",
                                label: "Radon",
                                color: "#FEC14B",
                                lines: [
                                  {
                                    color: "#FEC14B",
                                    value: 150,
                                    label: "150 Bq/m3 or lower",
                                  },
                                ],
                                unit: "Bq/m3",
                                x: "enqueuedTime_Stamp",
                                y: "Radon",
                                data: filteredHealth.Radon,
                                description:
                                  "Radon is a radioactive, invisible and odorless gas that occurs naturally in the environment. When radon is released from the ground outside, it is diluted and is not a concern. However, in buildings it can sometimes accumulate to high levels, which can pose a health risk.",
                              });
                              setSelectedKPI("Radon");
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <RadonKPI
                                  avgRadon={averages.Radon}
                                  maxRadon={max(
                                    filteredHealth.Radon,
                                    "Radon",
                                    true
                                  )}
                                  minRadon={min(
                                    filteredHealth.Radon,
                                    "Radon",
                                    true
                                  )}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {healthLoading && selectedAllRooms === "true" && (
                                <CircularProgress />
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                    </Swiper>
                  </SwiperSlide>

                  {/* Environmental Conditions */}
                  <SwiperSlide>
                    <Swiper
                      centeredSlides={true}
                      centeredSlidesBounds={true}
                      centerInsufficientSlides={true}
                      breakpoints={breakpoints}
                      pagination={{ clickable: true }}
                      scrollbar={{ draggable: true }}
                      onSlideChange={() => console.log("nested slide change")}
                      onSwiper={(swiper) => console.log(swiper)}
                    >
                      <SwiperSlide>
                        {/* Volatile Organic Gases Chart */}
                        {filteredHealth && health && (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "TVOC",
                                type: "indoor",
                                label: "Total volatile organic gases",
                                unit: "ppb",
                                lines: [
                                  {
                                    color: "green",
                                    value: 111,
                                    label: "111 ppb or lower",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "TVOC",
                                type: "indoor",
                                label: "Total volatile organic gases",
                                color: "#FEC14B",
                                lines: [
                                  {
                                    color: "green",
                                    value: 111,
                                    label: "111 ppb or lower",
                                  },
                                ],
                                unit: "ppb",
                                x: "enqueuedTime_Stamp",
                                y: "TVOC",
                                data: filteredHealth.TVOC,
                                description:
                                  "Volatile organic compounds (VOCs) are emitted as gases from certain solids or liquids. VOCs include a variety of chemicals, some of which may have short- and long-term adverse health effects.​",
                              });
                              setSelectedKPI("TVOC");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <AreaChart
                                    data={filteredHealth.TVOC}
                                    margin={{
                                      top: 30,
                                      right: 10,
                                      left: -40,
                                    }}
                                  >
                                    <defs>
                                      <linearGradient
                                        id="TVOC"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="5%"
                                          stopColor="#FEC14B"
                                          stopOpacity={0.8}
                                        />
                                        <stop
                                          offset="95%"
                                          stopColor="#FEC14B"
                                          stopOpacity={0}
                                        />
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="4 4" />
                                    <XAxis
                                      tick={false}
                                      dataKey="enqueuedTime_Stamp"
                                      tickFormatter={(unixTime) =>
                                        moment(unixTime).format("HH:mm Do") ===
                                        "Invalid date"
                                          ? ""
                                          : moment(unixTime).format("HH:mm Do")
                                      }
                                    />
                                    <YAxis tick={false} />
                                    <Tooltip
                                      wrapperClassName="navbar-bg"
                                      labelFormatter={(value) => {
                                        return `label: ${moment(value).format(
                                          "HH:mm Do"
                                        )}`;
                                      }}
                                    />
                                    <Legend
                                      verticalAlign="top"
                                      align="center"
                                      payload={[
                                        {
                                          value: "TVOC (ppb)",
                                          type: "line",
                                          id: "ID02",
                                          color: "#FEC14B",
                                        },
                                      ]}
                                    />
                                    <Area
                                      type="monotone"
                                      dataKey="TVOC"
                                      stroke="#FEC14B"
                                      dot={false}
                                      fillOpacity={1}
                                      fill="url(#TVOC)"
                                      label="TVOC"
                                    />
                                    <ReferenceLine
                                      y={111}
                                      stroke="green"
                                      strokeWidth={2}
                                      label={
                                        <Label
                                          fontWeight="bold"
                                          value="111 ppb or lower"
                                          fill={dark ? "white" : "black"}
                                        />
                                      }
                                    />
                                  </AreaChart>
                                </ResponsiveContainer>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )}
                      </SwiperSlide>
                      <SwiperSlide>
                        {/* Light Chart */}
                        {filteredHealth && health && (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "Light",
                                type: "indoor",
                                label: "Light",
                                unit: "lux",
                                areas: [
                                  {
                                    color: "green",
                                    y1: "320",
                                    y2: "500",
                                    label: "between 320 & 500 lux",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "Light",
                                label: "Light",
                                color: "#2BDF9E",
                                description:
                                  "Illuminance is the metric that is used to measure the light intensity within a space. It is measured in lux - it is the amount of light (lumens) falling on a surface (over any given square foot or square meter). Measuring the amount of light that falls on a surface allows us to evaluate if we have sufficient light to perform various visual tasks.",
                                areas: [
                                  {
                                    color: "green",
                                    y1: "320",
                                    y2: "500",
                                    label: "between 320 & 500 lux",
                                  },
                                ],
                                unit: "lux",
                                x: "enqueuedTime_Stamp",
                                y: "Light",

                                data: filteredHealth?.Light,
                              });
                              setSelectedKPI("Light");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <AreaChart
                                    data={filteredHealth?.Light}
                                    margin={{
                                      top: 30,
                                      right: 10,
                                      left: -40,
                                    }}
                                    width="100%"
                                    height="100%"
                                  >
                                    <defs>
                                      <linearGradient
                                        id="light"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="5%"
                                          stopColor="#2BDF9E"
                                          stopOpacity={0.8}
                                        />
                                        <stop
                                          offset="95%"
                                          stopColor="#2BDF9E"
                                          stopOpacity={0}
                                        />
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="4 4" />
                                    <XAxis
                                      // tick={{
                                      //   stroke: dark ? "#f3f3f3" : "",
                                      //   fill: dark ? "#f3f3f3" : "#666",
                                      //   strokeWidth: 0.5,
                                      // }}

                                      tick={false}
                                      dataKey="enqueuedTime_Stamp"
                                      tickFormatter={(unixTime) =>
                                        moment(unixTime).format("HH:mm Do") ===
                                        "Invalid date"
                                          ? ""
                                          : moment(unixTime).format("HH:mm Do")
                                      }
                                    />
                                    <YAxis
                                      // tick={{
                                      //   stroke: dark ? "#f3f3f3" : "",
                                      //   fill: dark ? "#f3f3f3" : "#666",
                                      //   strokeWidth: 0.5,
                                      // }}
                                      tick={false}
                                      max={100}
                                      // unit=' lx'
                                    />
                                    <Tooltip
                                      wrapperClassName="navbar-bg"
                                      labelFormatter={(value) => {
                                        return `label: ${moment(value).format(
                                          "HH:mm Do"
                                        )}`;
                                      }}
                                    />

                                    <Legend
                                      verticalAlign="top"
                                      align="center"
                                      payload={[
                                        {
                                          value: "Light (lux)",
                                          type: "line",
                                          id: "ID03",
                                          color: "#2BDF9E",
                                        },
                                      ]}
                                    />
                                    <Area
                                      type="monotone"
                                      dataKey="Light"
                                      stroke="#2BDF9E"
                                      fillOpacity={1}
                                      fill="url(#light)"
                                      dot={false}
                                    />
                                    <ReferenceArea
                                      y1={320}
                                      y2={500}
                                      stroke="green"
                                      strokeWidth={2}
                                      label={
                                        <Label
                                          value="Between 320 and 500 lux"
                                          fill={dark ? "white" : "black"}
                                          fontWeight="bold"
                                          style={{ fontSize: "12px" }}
                                        />
                                      }
                                    />
                                  </AreaChart>
                                </ResponsiveContainer>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )}
                      </SwiperSlide>
                      <SwiperSlide>
                        {/* Noise Level Chart */}
                        {filteredHealth && health && (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setChartName({
                                name: "Noise_Level",
                                type: "indoor",
                                label: "Noise Level",
                                unit: "db",
                                lines: [
                                  {
                                    color: "green",
                                    value: 60,
                                    label: "60 and Below",
                                  },
                                ],
                              });
                              setModalChart({
                                name: "Noise_Level",
                                type: "indoor",
                                label: "Noise",
                                color: "#05b4f0",
                                lines: [
                                  {
                                    color: "green",
                                    value: 60,
                                    label: "60 and Below",
                                  },
                                ],
                                unit: "db",
                                x: "enqueuedTime_Stamp",
                                y: "Noise_Level",
                                data: filteredHealth.Noise_Level,
                                description:
                                  "The amplitude level of the undesired background noise, Noise level is measured in decibels (dB). The louder the noise, the higher the decibels. Decibels can be adjusted to human hearing. Noise level is thus described in decibels A (dBA). The effects of noise vary with the noise to which a person is exposed.",
                              });
                              setSelectedKPI("Noise_Level");
                              setSelectedPattern(4);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            {/* 
                          <Card sx={cardStyle}>
                            <CardContent sx={cardContentStyle}>
                              <ResponsiveContainer
                                width="100%" height="100%"
                              >
                                <AreaChart
                                  data={filteredHealth.TVOC}
                                  margin={{ 
                                    top:30,
                                    right:10, left:-40}}
                                > */}
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <AreaChart
                                    data={filteredHealth.Noise_Level}
                                    margin={{
                                      top: 30,
                                      right: 10,
                                      left: -40,
                                    }}
                                    width="100%"
                                    height="100%"
                                  >
                                    <defs>
                                      <linearGradient
                                        id="noise"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="5%"
                                          stopColor="#05b4f0"
                                          stopOpacity={0.8}
                                        />
                                        <stop
                                          offset="95%"
                                          stopColor="#05b4f0"
                                          stopOpacity={0}
                                        />
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="4 4" />
                                    <XAxis
                                      // tick={{
                                      //   stroke: dark ? "#f3f3f3" : "",
                                      //   fill: dark ? "#f3f3f3" : "#666",
                                      //   strokeWidth: 0.5,
                                      // }}
                                      tick={false}
                                      dataKey="enqueuedTime_Stamp"
                                      tickFormatter={(unixTime) =>
                                        moment(unixTime).format("HH:mm Do") ===
                                        "Invalid date"
                                          ? ""
                                          : moment(unixTime).format("HH:mm Do")
                                      }
                                    />
                                    <YAxis
                                      // tick={{
                                      //   stroke: dark ? "#f3f3f3" : "",
                                      //   fill: dark ? "#f3f3f3" : "#666",
                                      //   strokeWidth: 0.5,
                                      // }}
                                      tick={false}
                                      max={100}
                                      // unit=' db'
                                    />
                                    <Tooltip
                                      wrapperClassName="navbar-bg"
                                      labelFormatter={(value) => {
                                        return `label: ${moment(value).format(
                                          "HH:mm Do"
                                        )}`;
                                      }}
                                    />

                                    <Legend
                                      verticalAlign="top"
                                      payload={[
                                        {
                                          value: "Noise (db)",
                                          type: "line",
                                          id: "ID02",
                                          color: "#05b4f0",
                                        },
                                      ]}
                                    />
                                    <Area
                                      type="monotone"
                                      dataKey="Noise_Level"
                                      stroke="#05b4f0"
                                      fillOpacity={1}
                                      fill="url(#noise)"
                                      dot={false}
                                    />
                                    <ReferenceLine
                                      y={60}
                                      stroke="green"
                                      strokeWidth={2}
                                      label={
                                        <Label
                                          value="60 and Below"
                                          fill={dark ? "white" : "black"}
                                          fontWeight="bold"
                                          style={{ fontSize: "12px" }}
                                        />
                                      }
                                      // strokeDasharray='4 4'
                                    />
                                  </AreaChart>
                                </ResponsiveContainer>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )}
                      </SwiperSlide>
                    </Swiper>
                  </SwiperSlide>
                </>

                {building?.id === 1 && (
                  <>
                    {/* Outdoor */}
                    <SwiperSlide className="">
                      <Swiper
                        centeredSlides={true}
                        centeredSlidesBounds={true}
                        centerInsufficientSlides={true}
                        breakpoints={breakpoints}
                        pagination={{ clickable: true }}
                        scrollbar={{ draggable: true }}
                        onSlideChange={() => console.log("nested slide change")}
                        onSwiper={(swiper) => console.log(swiper)}
                      >
                        <SwiperSlide>
                          {/* Outdoor Temperature KPI */}
                          {filteredOutdoor && outdoor && (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setChartName({
                                  name: "OutdoorTemperature",
                                  type: "outdoor",
                                  label: "Outdoor Temperature",
                                  unit: "°C",
                                  areas: [
                                    {
                                      color: "green",
                                      y1: "20",
                                      y2: "24",
                                      label: "between 20 & 24 C",
                                    },
                                  ],
                                });
                                setModalChart({
                                  name: "OutdoorTemperature",
                                  label: "Outdoor Temperature",
                                  type: "outdoor",
                                  color: "#FEC14B",
                                  unit: "°C",
                                  x: "enqueuedTime_Stamp",
                                  y: "Temperature",
                                  description:
                                    "It's the outdoor temperature , that measured to reflects the employee comfort.",
                                  areas: [
                                    {
                                      color: "green",
                                      y1: "20",
                                      y2: "24",
                                      label: "between 20 & 24 C",
                                    },
                                  ],
                                  data: filteredOutdoor.Temperature,
                                });
                                setSelectedKPI("none");
                                setSelectedPattern(4);
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <OutdoorTemperatureKPI
                                    avgTemp={averages.TemperatureOutdoor}
                                    maxTemp={max(
                                      filteredOutdoor.Temperature,
                                      "Temperature",
                                      true
                                    )}
                                    minTemp={min(
                                      filteredOutdoor.Temperature,
                                      "Temperature",
                                      true
                                    )}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          )}
                        </SwiperSlide>
                        <SwiperSlide>
                          {/* Outdoor AQI KPI */}
                          {filteredOutdoor && outdoor ? (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setChartName({
                                  name: "OutdoorAQI",
                                  type: "outdoor",
                                  label: "Outdoor Air Quality",
                                  unit: "",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 200,
                                      label: "50 and Below",
                                    },
                                  ],
                                });
                                setModalChart({
                                  name: "OutdoorAQI",
                                  type: "outdoor",
                                  label: "Outdoor Air Quality",
                                  color: "#FEC14B",
                                  description:
                                    "Outdoor Air Quality (OAQ) refers to the air quality within and around buildings and structures, especially as it relates to the health and comfort of building occupants. Understanding and controlling common pollutants indoors can help reduce your risk of indoor health concerns.",
                                  unit: "",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 200,
                                      label: "50 and Below",
                                    },
                                  ],
                                  x: "enqueuedTime_Stamp",
                                  y: "OutdoorAQI",
                                  data: filteredOutdoor.OutdoorAQI,
                                });
                                setSelectedKPI("none");
                                setSelectedPattern(4);
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <OutdoorAQIKPI
                                    avgAQI={averages.OutdoorAQI}
                                    maxAQI={max(
                                      filteredOutdoor.OutdoorAQI,
                                      "OutdoorAQI",
                                      true
                                    )}
                                    minAQI={min(
                                      filteredOutdoor.OutdoorAQI,
                                      "OutdoorAQI",
                                      true
                                    )}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          ) : (
                            <Card sx={cardStyle}>
                              <CardContent>
                                <CircularProgress />
                              </CardContent>
                            </Card>
                          )}
                        </SwiperSlide>
                        <SwiperSlide>
                          {/* Outdoor Humidity KPI */}
                          {filteredOutdoor && outdoor ? (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setChartName({
                                  name: "Relative_HumidityOutdoor",
                                  type: "outdoor",
                                  label: "Outdoor Humidity",
                                  unit: "",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 200,
                                      label: "50 and Below",
                                    },
                                  ],
                                });
                                setModalChart({
                                  name: "Relative_HumidityOutdoor",
                                  type: "outdoor",
                                  label: "Outdoor Humidity",
                                  color: "#FEC14B",
                                  description:
                                    "Relative humidity (RH) (expressed as a percent) also measures water vapor, but RELATIVE to the temperature of the air. In other words, it is a measure of the actual amount of water vapor in the air compared to the total amount of vapor that can exist in the air at its current temperature.",
                                  unit: "",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 200,
                                      label: "50 and Below",
                                    },
                                  ],
                                  x: "enqueuedTime_Stamp",
                                  y: "Relative_Humidity",
                                  data: filteredOutdoor.Relative_Humidity,
                                });
                                setSelectedKPI("none");
                                setSelectedPattern(4);
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <OutdoorHumidityKPI
                                    avgHum={averages.Relative_Humidity}
                                    maxHum={max(
                                      filteredOutdoor.Relative_Humidity,
                                      "Relative_Humidity",
                                      true
                                    )}
                                    minHum={min(
                                      filteredOutdoor.Relative_Humidity,
                                      "Relative_Humidity",
                                      true
                                    )}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          ) : (
                            <Card sx={cardStyle}>
                              <CardContent>
                                <CircularProgress />
                              </CardContent>
                            </Card>
                          )}
                        </SwiperSlide>
                        <SwiperSlide>
                          {/* Outdoor PM10 KPI */}
                          {filteredOutdoor && outdoor ? (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setChartName({
                                  name: "OutdoorPM10",
                                  type: "outdoor",
                                  label: "Outdoor PM10",
                                  unit: "",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 200,
                                      label: "50 and Below",
                                    },
                                  ],
                                });
                                setModalChart({
                                  name: "OutdoorPM10",
                                  type: "outdoor",
                                  label: "PM10",
                                  color: "#FEC14B",
                                  description:
                                    "PM10 refers to particulate matter that is less than 10 micrometers in diameter. These particles are small enough to be inhaled and can reach the respiratory system, leading to health problems such as asthma, bronchitis, and other respiratory issues.",
                                  unit: "(μ g/m ³)",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 15,
                                      label: "15 μ g/m ³ or Below",
                                    },
                                  ],
                                  x: "enqueuedTime_Stamp",
                                  y: "PM10",
                                  data: filteredOutdoor.PM10,
                                });
                                setSelectedKPI("none");
                                setSelectedPattern(4);
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <OutdoorPM10KPI
                                    avgHum={averages.OutdoorPM10}
                                    maxHum={max(
                                      filteredOutdoor.PM10,
                                      "PM10",
                                      true
                                    )}
                                    minHum={min(
                                      filteredOutdoor.PM10,
                                      "PM10",
                                      true
                                    )}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          ) : (
                            <Card sx={cardStyle}>
                              <CardContent>
                                <CircularProgress />
                              </CardContent>
                            </Card>
                          )}
                        </SwiperSlide>
                        <SwiperSlide>
                          {/* Outdoor PM25 KPI */}
                          {filteredOutdoor && outdoor ? (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setChartName({
                                  name: "OutdoorPM25",
                                  type: "outdoor",
                                  label: "Outdoor PM25",
                                  unit: "",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 200,
                                      label: "50 and Below",
                                    },
                                  ],
                                });
                                setModalChart({
                                  name: "OutdoorPM25",
                                  type: "outdoor",
                                  label: "PM2.5",
                                  color: "#FEC14B",
                                  description:
                                    "PM2.5 refers to particulate matter that is less than 2.5 micrometers in diameter. These fine particles are small enough to be deeply inhaled into the lungs and can even enter the bloodstream.",
                                  unit: "(μ g/m ³)",
                                  lines: [
                                    {
                                      color: "green",
                                      value: 15,
                                      label: "15 μ g/m ³ or Below",
                                    },
                                  ],
                                  x: "enqueuedTime_Stamp",
                                  y: "PM25",
                                  data: filteredOutdoor.PM25,
                                });
                                setSelectedKPI("none");
                                setSelectedPattern(4);
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <OutdoorPM25KPI
                                    avgHum={averages.OutdoorPM25}
                                    maxHum={max(
                                      filteredOutdoor.PM25,
                                      "PM25",
                                      true
                                    )}
                                    minHum={min(
                                      filteredOutdoor.PM25,
                                      "PM25",
                                      true
                                    )}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          ) : (
                            <Card sx={cardStyle}>
                              <CardContent>
                                <CircularProgress />
                              </CardContent>
                            </Card>
                          )}
                        </SwiperSlide>
                      </Swiper>
                    </SwiperSlide>

                    {/* Patterns */}
                    <SwiperSlide>
                      <Swiper
                        centeredSlides={true}
                        centeredSlidesBounds={true}
                        centerInsufficientSlides={true}
                        breakpoints={breakpoints}
                        pagination={{ clickable: true }}
                        scrollbar={{ draggable: true }}
                        onSlideChange={() => console.log("nested slide change")}
                        onSwiper={(swiper) => console.log(swiper)}
                      >
                        <SwiperSlide>
                          {filteredHealth && health && (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setSelectedPattern(1);
                                setSelectedKPI("none");
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <h5>Productivity</h5>
                                  <img
                                    src="/images/icons/productivity-selected.png"
                                    alt=""
                                    width={100}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          )}
                        </SwiperSlide>
                        <SwiperSlide>
                          {filteredHealth && health && (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setSelectedPattern(2);
                                setSelectedKPI("none");
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <h5>Vaping</h5>
                                  <img
                                    src="/images/icons/vaping-selected.png"
                                    alt=""
                                    width={100}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          )}
                        </SwiperSlide>
                        <SwiperSlide>
                          {/* Efficiency */}
                          {filteredHealth && health && (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setSelectedPattern(3);
                                setSelectedKPI("none");
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  <h5>Efficiency</h5>
                                  <img
                                    src="/images/icons/efficiency-selected.png"
                                    alt=""
                                    width={100}
                                  />
                                </CardContent>
                              </Card>
                            </motion.div>
                          )}
                        </SwiperSlide>
                        <SwiperSlide>
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setSelectedPattern(4)}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <img
                              src="/images/icons/off-selected.png"
                              alt=""
                              width={100}
                            />
                          </motion.div>
                        </SwiperSlide>
                      </Swiper>
                    </SwiperSlide>
                  </>
                )}
              </Swiper>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Health;
